import { tokens } from '@/design';
import { ShapeMarkerArtwork } from './markerShapes';
import { northArrowRotationDeg } from './northArrowRotation';
import type {
  NorthArrowHalo,
  NorthArrowMarkerLayer,
  NorthArrowStyleId,
} from './layoutTypes';

export interface LayoutNorthArrowProps {
  readonly styleId: NorthArrowStyleId;
  readonly bearingDeg: number;
  readonly rotationDeg?: number;
  /** Symbol ink. `null` is the palette's "No color"; omitted uses page ink. */
  readonly color?: string | null;
  readonly marker?: NorthArrowMarkerLayer | null;
  readonly halo?: NorthArrowHalo | null;
}

export const NORTH_ARROW_STYLES: readonly {
  readonly id: NorthArrowStyleId;
  readonly label: string;
}[] = [
  { id: 'arcgisNorth1', label: 'ArcGIS North 1' },
  { id: 'arcgisNorth2', label: 'ArcGIS North 2' },
  { id: 'arcgisNorth3', label: 'ArcGIS North 3' },
  { id: 'needle', label: 'Needle' },
  { id: 'flag', label: 'Flag' },
  { id: 'survey', label: 'Survey' },
  { id: 'marker', label: 'Marker' },
  { id: 'slender', label: 'Slender' },
  { id: 'solid', label: 'Solid' },
  { id: 'ring', label: 'Ring' },
  { id: 'compass', label: 'Compass' },
  { id: 'compassRose', label: 'Compass rose' },
  { id: 'starRose', label: 'Star rose' },
  { id: 'fourPoint', label: 'Four point' },
  { id: 'quadrantRose', label: 'Quadrant rose' },
  { id: 'ornateCircle', label: 'Ornate circle' },
  { id: 'nauticalRose', label: 'Nautical rose' },
  { id: 'blockArrow', label: 'Block north' },
  { id: 'splitBlockArrow', label: 'Split block north' },
  { id: 'simpleCompass', label: 'Simple compass' },
  { id: 'trueNorth', label: 'True North' },
  { id: 'magneticNorth1', label: 'Magnetic North 1' },
  { id: 'magneticNorth2', label: 'Magnetic North 2' },
  { id: 'gridNorth', label: 'Grid North' },
  { id: 'simpleCircleFilled', label: 'Simple filled circle' },
  { id: 'simpleCircleOutline', label: 'Simple outline circle' },
  { id: 'detailedRose', label: 'Detailed rose' },
];

function NorthMark({ styleId }: { readonly styleId: NorthArrowStyleId }) {
  switch (styleId) {
    case 'arcgisNorth1':
      return <>
        <text x="32" y="14" textAnchor="middle" fontSize="13" fontWeight="700">N</text>
        <path d="M32 18 49 62 32 51 15 62Z" fill="currentColor" />
      </>;
    case 'arcgisNorth2':
      return <>
        <text x="32" y="13" textAnchor="middle" fontSize="12" fontWeight="700">N</text>
        <path d="M32 17 49 62 32 50 15 62Z" fill="currentColor" />
        <path d="M32 17 32 50 22 57Z" fill={tokens.color.canvas.light} />
      </>;
    case 'arcgisNorth3':
      return <>
        <path d="M32 5 48 59 32 48 16 59Z" fill="none" stroke="currentColor" strokeWidth="2.5" />
        <path d="M32 5 32 48 21 55Z" fill="currentColor" />
        <text x="32" y="76" textAnchor="middle" fontSize="14" fontWeight="700">N</text>
      </>;
    case 'needle':
      return <>
        <text x="32" y="11" textAnchor="middle" fontSize="10" fontWeight="700">N</text>
        <path d="M32 14 38 43 32 38 26 43Z" fill="currentColor" />
        <path d="M32 70 27 42 32 47 37 42Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="32" cy="43" r="3" fill={tokens.color.canvas.light} stroke="currentColor" />
      </>;
    case 'flag':
      return <>
        <path d="M28 70V12M28 14l19 8-14 5 14 8-19-3" fill="none" stroke="currentColor" strokeWidth="3" />
        <text x="17" y="48" textAnchor="middle" fontSize="14" fontWeight="700">N</text>
      </>;
    case 'survey':
      return <>
        <path d="M32 6v64M32 8l7 26-7-6-7 6Z" fill="currentColor" />
        <path d="M22 54 42 30" fill="none" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="32" cy="39" r="3" fill={tokens.color.canvas.light} stroke="currentColor" />
      </>;
    case 'marker':
      return <>
        <path d="M32 7v65M32 8l8 17-8-5-8 5Z" fill="currentColor" />
        <circle cx="32" cy="45" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="32" cy="45" r="2" fill="currentColor" />
        <text x="23" y="18" fontSize="10" fontWeight="700">N</text>
      </>;
    case 'slender':
      return <>
        <text x="32" y="12" textAnchor="middle" fontSize="10" fontWeight="700">N</text>
        <path d="M32 14 39 69 32 56 25 69Z" fill="currentColor" />
        <path d="M32 14v42l-5 10Z" fill={tokens.color.canvas.light} />
      </>;
    case 'solid':
      return <>
        <text x="32" y="13" textAnchor="middle" fontSize="11" fontWeight="700">N</text>
        <path d="M32 16 43 70 32 61 21 70Z" fill="currentColor" />
      </>;
    case 'ring':
      return <>
        <path d="M32 4 43 25 36 23v13h-8V23l-7 2Z" fill="currentColor" />
        <circle cx="32" cy="51" r="18" fill="none" stroke="currentColor" strokeWidth="5" />
        <text x="32" y="59" textAnchor="middle" fontSize="23" fontWeight="700">N</text>
      </>;
    case 'compassRose':
      return <>
        <text x="32" y="10" textAnchor="middle" fontSize="9" fontWeight="700">N</text>
        <path d="M32 11 38 36 32 31 26 36ZM32 69 26 36 32 41 38 36Z" fill="currentColor" />
        <path d="M5 38 32 32 27 38 32 44ZM59 38 32 32 37 38 32 44Z" fill="currentColor" opacity=".55" />
        <circle cx="32" cy="38" r="4" fill={tokens.color.canvas.light} stroke="currentColor" />
      </>;
    case 'starRose':
      return <>
        <circle cx="32" cy="39" r="24" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M32 5 38 33 59 39 38 45 32 73 26 45 5 39 26 33Z" fill="currentColor" />
        <path d="M32 5v34L5 39h27v34-34h27" fill={tokens.color.canvas.light} stroke="currentColor" strokeWidth="1" />
        <text x="32" y="13" textAnchor="middle" fontSize="8" fontWeight="700">N</text>
      </>;
    case 'fourPoint':
      return <>
        <path d="M32 5 39 32 59 39 39 46 32 73 25 46 5 39 25 32Z" fill="currentColor" />
        <path d="M32 5v34H5m27 0v34m0-34h27" fill="none" stroke={tokens.color.canvas.light} strokeWidth="1.4" />
        <text x="32" y="13" textAnchor="middle" fontSize="8" fontWeight="700">N</text>
        <text x="8" y="36" fontSize="7" fontWeight="700">W</text>
        <text x="51" y="36" fontSize="7" fontWeight="700">E</text>
      </>;
    case 'quadrantRose':
      return <>
        <circle cx="32" cy="39" r="22" fill="none" stroke="currentColor" />
        <path d="M32 5 38 34 59 39 38 44 32 73 26 44 5 39 26 34Z" fill="currentColor" />
        <path d="M32 13 34 37 51 39 34 41 32 65 30 41 13 39 30 37Z" fill={tokens.color.canvas.light} />
        <circle cx="32" cy="39" r="5" fill="currentColor" />
        <text x="32" y="12" textAnchor="middle" fontSize="7" fontWeight="700">N</text>
        <text x="6" y="36" fontSize="6">W</text>
        <text x="54" y="36" fontSize="6">E</text>
        <text x="32" y="78" textAnchor="middle" fontSize="6">S</text>
      </>;
    case 'ornateCircle':
      return <>
        <circle cx="32" cy="40" r="27" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="32" cy="40" r="20" fill="none" stroke="currentColor" />
        <path d="M32 5 37 34 59 40 37 46 32 75 27 46 5 40 27 34Z" fill="currentColor" />
        <path d="M32 14v52M10 40h44M16 24l32 32M48 24 16 56" stroke={tokens.color.canvas.light} strokeWidth="1" />
        <circle cx="32" cy="40" r="6" fill={tokens.color.canvas.light} stroke="currentColor" />
      </>;
    case 'nauticalRose':
      return <>
        <circle cx="32" cy="40" r="25" fill="none" stroke="currentColor" />
        <circle cx="32" cy="40" r="17" fill="none" stroke="currentColor" strokeDasharray="2 2" />
        <path d="M32 5 38 35 59 40 38 45 32 75 26 45 5 40 26 35Z" fill="currentColor" opacity=".8" />
        <path d="M32 13 34 38 51 40 34 42 32 67 30 42 13 40 30 38Z" fill={tokens.color.canvas.light} />
        <text x="32" y="12" textAnchor="middle" fontSize="7" fontWeight="700">N</text>
        <text x="8" y="38" fontSize="6">W</text>
        <text x="53" y="38" fontSize="6">E</text>
      </>;
    case 'blockArrow':
      return <>
        <path d="M32 5 56 34H44v34H20V34H8Z" fill="currentColor" />
        <path d="M25 68h14v7H25Z" fill="currentColor" />
        <text x="32" y="61" textAnchor="middle" fontSize="17" fill={tokens.color.canvas.light} fontWeight="700">N</text>
      </>;
    case 'splitBlockArrow':
      return <>
        <path d="M32 5 53 61H37v13H27V61H11Z" fill="currentColor" />
        <path d="M32 5v56H17Z" fill={tokens.color.canvas.light} />
        <path d="M32 5 53 61H37v13H27V61H11Z" fill="none" stroke="currentColor" strokeWidth="2" />
      </>;
    case 'simpleCompass':
      return <>
        <path d="M32 8v64M4 40h56M12 20l40 40M52 20 12 60" stroke="currentColor" strokeWidth="1.2" />
        <text x="32" y="7" textAnchor="middle" fontSize="7" fontWeight="700">N</text>
        <text x="2" y="38" fontSize="6">W</text>
        <text x="57" y="38" fontSize="6">E</text>
        <text x="32" y="79" textAnchor="middle" fontSize="6">S</text>
        <circle cx="32" cy="40" r="2" fill="currentColor" />
      </>;
    case 'trueNorth':
      return <>
        <text x="32" y="10" textAnchor="middle" fontSize="9" fontWeight="700">TN</text>
        <path d="M32 14v56" stroke="currentColor" strokeWidth="2" />
        <path d="m32 13 2 5 5 .5-4 3 1.5 5-4.5-3-4.5 3 1.5-5-4-3 5-.5Z" fill="currentColor" />
      </>;
    case 'magneticNorth1':
      return <>
        <text x="32" y="10" textAnchor="middle" fontSize="9" fontWeight="700">MN</text>
        <path d="M32 16v55" stroke="currentColor" strokeWidth="2" />
        <path d="M32 15 39 30 32 26Z" fill="currentColor" />
      </>;
    case 'magneticNorth2':
      return <>
        <text x="32" y="10" textAnchor="middle" fontSize="9" fontWeight="700">MN</text>
        <path d="M32 16v55" stroke="currentColor" strokeWidth="2" />
        <path d="M32 15 25 30 32 26Z" fill="currentColor" />
      </>;
    case 'gridNorth':
      return <>
        <text x="32" y="11" textAnchor="middle" fontSize="9" fontWeight="700">GN</text>
        <path d="M32 16v55" stroke="currentColor" strokeWidth="2" />
      </>;
    case 'simpleCircleFilled':
      return <>
        <circle cx="32" cy="40" r="28" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M32 12 47 62 32 52 17 62Z" fill="currentColor" />
      </>;
    case 'simpleCircleOutline':
      return <>
        <circle cx="32" cy="40" r="28" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M32 12 47 62 32 52 17 62Z" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M32 12v40" stroke="currentColor" />
      </>;
    case 'detailedRose':
      return <>
        <circle cx="32" cy="40" r="27" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="32" cy="40" r="22" fill="none" stroke="currentColor" strokeDasharray="1 2" />
        <path d="M32 4 37 34 60 40 37 46 32 76 27 46 4 40 27 34Z" fill="currentColor" />
        <path d="M32 11 34 38 53 40 34 42 32 69 30 42 11 40 30 38Z" fill={tokens.color.canvas.light} />
        <circle cx="32" cy="40" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
      </>;
    case 'compass':
    default:
      return <>
        <circle cx="32" cy="39" r="21" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M32 7 38 36 32 31 26 36Z" fill="currentColor" />
        <path d="M32 71 26 42 32 47 38 42Z" fill="currentColor" opacity=".45" />
        <path d="M3 39h58M32 10v58" stroke="currentColor" strokeWidth="1" />
        <text x="32" y="16" textAnchor="middle" fontSize="9" fontWeight="700">N</text>
        <text x="4" y="36" fontSize="7">W</text>
        <text x="54" y="36" fontSize="7">E</text>
        <text x="32" y="78" textAnchor="middle" fontSize="7">S</text>
      </>;
  }
}

function SymbolArtwork({
  styleId,
  marker,
  paint,
}: {
  readonly styleId: NorthArrowStyleId;
  readonly marker?: NorthArrowMarkerLayer | null;
  readonly paint?: string;
}) {
  if (marker && !marker.visible) return null;
  if (!marker || marker.source === 'style') {
    return <NorthMark styleId={styleId} />;
  }
  return (
    <ShapeMarkerArtwork
      marker={
        paint === undefined
          ? marker
          : {
              ...marker,
              fillColor: paint,
              outlineColor: paint,
              outlineWidthPt: 0,
            }
      }
    />
  );
}

export function LayoutNorthArrow({
  styleId,
  bearingDeg,
  rotationDeg,
  color,
  marker,
  halo,
}: LayoutNorthArrowProps) {
  const rotate = rotationDeg ?? northArrowRotationDeg(bearingDeg);
  const ink = color === undefined
    ? tokens.color.ink.inverse
    : color ?? 'transparent';
  // Halo is painted as a stroked copy under the mark; the outline is a wider
  // copy under that, matching ArcGIS's halo-then-outline stacking.
  const haloWidth = halo?.enabled ? Math.max(0, halo.sizePt) : 0;
  return (
    <svg
      viewBox="0 0 64 80"
      width="100%"
      height="100%"
      aria-label={`North arrow, ${Math.round(rotate)} degrees`}
      data-marker-source={marker?.source ?? 'style'}
      data-marker-form={marker?.source === 'form' ? marker.formId : undefined}
      style={{
        color: ink,
        fontFamily: tokens.font.family.ui,
        transform: `rotate(${rotate}deg)`,
        transformOrigin: 'center',
        overflow: 'visible',
      }}
    >
      {haloWidth > 0 && halo ? (
        <>
          <g
            aria-hidden
            fill={halo.outlineColor}
            stroke={halo.outlineColor}
            strokeWidth={haloWidth * 2}
            strokeLinejoin="round"
            strokeLinecap="round"
          >
            <SymbolArtwork styleId={styleId} marker={marker} paint={halo.outlineColor} />
          </g>
          <g
            aria-hidden
            fill={halo.color}
            stroke={halo.color}
            strokeWidth={haloWidth}
            strokeLinejoin="round"
            strokeLinecap="round"
          >
            <SymbolArtwork styleId={styleId} marker={marker} paint={halo.color} />
          </g>
        </>
      ) : null}
      <SymbolArtwork styleId={styleId} marker={marker} />
    </svg>
  );
}
